import { Checkbox, CommandBar, ICommandBarItemProps, Icon, mergeStyles,Text } from '@fluentui/react';
import * as React from 'react';
import { useContext, useEffect,useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import * as Constants from '../../../../../Common/CommonModels/Constants';
import { CDMRecord } from '../../../../../Common/DataOwnerModels/CDMRecord';
import { DataOwnerRequestContext } from '../../../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import { AddExistingCdmForm } from './AddExistingCdmForm';
import { EditCdmForm } from './EditCdmForm';

export function CdmStep(): React.ReactElement {
  // Data owner Request Context
  const {dataOwnerRequestDetails} = useContext(DataOwnerRequestContext);
  const cdmListFromContext = dataOwnerRequestDetails?.cdms;
  const initialPageFlag: boolean = cdmListFromContext != null && cdmListFromContext.length > 0 ? false : true;

  // CDM List State
  const [cdmList, setCdmList] = useState<CDMRecord[]>(cdmListFromContext);

  // Command Bar State
  const [isRemoveCdmButtonDisabled, setIsRemoveCdmButtonDisabled] = useState<boolean>(true);
  const [isAddExistingCdmButtonDisabled, setIsAddExistingCdmButtonDisabled] = useState<boolean>(true);

  // Form State
  const [isInitialPageVisible, setIsIntialPageVisible] = useState<boolean>(initialPageFlag);
  const [isAddExistingCdmFormVisible, setIsAddExistingCdmFormVisible] = useState<boolean>(false);
  const [cdmIdDropdownList, setCdmIdDropdownList] = useState<string[]>([]);
  const [cdmIdCheckedList, setCdmIdCheckedList] = useState<string[]>([]);

  useEffect(() => {
    setCdmList(cdmListFromContext);
  }, [cdmListFromContext]);

  const dropdownIconClass = mergeStyles({
    fontSize: 20,
    height: 20,
    width: 20,
    color: '#242424',
  });

  const checkBoxStyles = {
    text: {
      padding: '0px 0px 0px 4px',
    },
  };

  useEffect(() => {
    if (dataOwnerRequestDetails?.request.environment == 0) {
      if (dataOwnerRequestDetails?.nonProdApprovedCdms?.length <= 0) {
        setIsAddExistingCdmButtonDisabled(true);
      } else {
        setIsAddExistingCdmButtonDisabled(false);
      }
    } else if (dataOwnerRequestDetails?.request.environment == 1) {
      if (dataOwnerRequestDetails?.prodApprovedCdms?.length <= 0) {
        setIsAddExistingCdmButtonDisabled(true);
      } else {
        setIsAddExistingCdmButtonDisabled(false);
      }
    }
  }, [dataOwnerRequestDetails?.nonProdApprovedCdms, dataOwnerRequestDetails?.prodApprovedCdms]);

  const initialCdmCommandBarItems = [
    {
      key: 'addCdm',
      text: 'Add CDM',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        handleClickAddCdm();
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
    {
      key: 'addExistingCdm',
      text: 'Add Existing CDM',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        handleClickAddExistingCdm();
      },
      disabled: isAddExistingCdmButtonDisabled,
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 16px',
        },
      },
    },
  ];

  const inProcessCdmCommandBarItems: ICommandBarItemProps[] = [
    {
      key: 'addCdm',
      text: 'Add CDM',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        handleClickAddCdm();
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
    {
      key: 'removeCdm',
      text: 'Remove CDM',
      iconProps: { iconName: 'Delete' },
      disabled: isRemoveCdmButtonDisabled,
      onClick: () => {
        handleClickRemoveCdm();
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 16px',
        },
      },
    },
    {
      key: 'addExistingCdm',
      text: 'Add Existing CDM',
      iconProps: { iconName: 'Add' },
      disabled: isAddExistingCdmButtonDisabled,
      onClick: () => {
        handleClickAddExistingCdm();
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 16px',
        },
      },
    },
  ];

  const handleClickAddCdm = () => {
    setIsIntialPageVisible(false);
    const newId = uuidv4();
    const newCdm: CDMRecord = {
      id: newId,
      name: 'New CDM',
      tenantId: dataOwnerRequestDetails.dataOwnerId,
      tenantName: dataOwnerRequestDetails.dataOwnerName,
      status: 'Draft',
      type: Constants.CDM.cdmType1OptionKey,
      category: 'CDM',
      version: 1,
      description: '',
      egressOptions: [],
      schema: '',
      expression: null,
      cardinality: 0,
      ingestionType: Constants.CDM.IngestionType_Standard,
    };
    dataOwnerRequestDetails.addCdmList(newCdm);
    setCdmIdDropdownList([newId]);
  };

  const handleClickRemoveCdm = () => {
    // Delete CDMs from Context object
    dataOwnerRequestDetails.deleteCdm(cdmIdCheckedList);

    // Deletion list: Only Saved CDMs in the storage needs to be deleted by API. So, ignoring the CDMs that have been added in the Requested & deleted before Saving.
    const savedCdmDeletionList = cdmIdCheckedList.filter((x) => dataOwnerRequestDetails.savedCdmIdList.includes(x));
    dataOwnerRequestDetails.updateDeleteCdmList(dataOwnerRequestDetails.cdmDeletionList.concat(savedCdmDeletionList));

    const newCdmIdDropdownList: string[] = cdmIdDropdownList.filter((cdmIdDropdown) => {
      return cdmIdCheckedList.includes(cdmIdDropdown) == false;
    });
    setCdmIdDropdownList(newCdmIdDropdownList);
    setCdmIdCheckedList([]);
    setIsRemoveCdmButtonDisabled(true);

    // TODO: Consider the edge case of deleting a CDM that is used in the Rule Step
  };

  const handleClickAddExistingCdm = () => {
    setIsIntialPageVisible(false);
    setIsAddExistingCdmFormVisible(true);
    setCdmIdDropdownList([]);
  };

  const handleCancelAddExistingCdm = () => {
    setIsAddExistingCdmFormVisible(false);
  };

  const onChangeCdmDropdown = (cdm: CDMRecord) => {
    const newCdmIdDropdownList: string[] = JSON.parse(
      JSON.stringify(cdmIdDropdownList) // deep copy
    );
    if (cdmIdDropdownList.includes(cdm.id)) {
      const cdmIdDropdownIndex: number = cdmIdDropdownList.indexOf(cdm.id);
      newCdmIdDropdownList.splice(cdmIdDropdownIndex, 1);
    } else {
      newCdmIdDropdownList.push(cdm.id);
    }
    setCdmIdDropdownList(newCdmIdDropdownList);
  };

  const onChangeSelectSingleCheckBox = (checked: boolean, cdm: CDMRecord) => {
    const newCdmIdCheckedList: string[] = JSON.parse(
      JSON.stringify(cdmIdCheckedList) // deep copy
    );
    if (checked) {
      newCdmIdCheckedList.push(cdm.id);
    } else {
      const removeIndex = newCdmIdCheckedList.indexOf(cdm.id);
      newCdmIdCheckedList.splice(removeIndex, 1);
    }
    if (newCdmIdCheckedList.length > 0) {
      setIsRemoveCdmButtonDisabled(false);
    } else {
      setIsRemoveCdmButtonDisabled(true);
    }
    setCdmIdCheckedList(newCdmIdCheckedList);
  };

  const renderCdmDropdown = (cdm: CDMRecord) => {
    const isChecked = cdmIdCheckedList.includes(cdm.id);
    const isDropdownExpand = cdmIdDropdownList.includes(cdm.id);

    let displayCdmName: string = cdm.name;
    const maxCdmNameLength = 50;
    if (displayCdmName?.length > maxCdmNameLength) {
      displayCdmName = displayCdmName.substring(0, maxCdmNameLength);
      displayCdmName = displayCdmName.concat(' [...]');
    }

    return (
      <>
        <SingleCdmContainer>
          <CheckTextIconContainer>
            <CheckTextContainer>
              <Checkbox
                key={cdm.id}
                id={cdm.id}
                ariaLabel={`${cdm.name} Check Box`}
                checked={isChecked}
                onChange={(ev, checked): void => onChangeSelectSingleCheckBox(checked, cdm)}
                styles={checkBoxStyles}
              />
              <SingleCdmTextStyled>{displayCdmName}</SingleCdmTextStyled>
            </CheckTextContainer>
            {isDropdownExpand && (
              <Icon
                data-testid="contract-cdm"
                aria-label="Contract CDM"
                iconName="ChevronUp"
                className={dropdownIconClass}
                onClick={() => onChangeCdmDropdown(cdm)}
              />
            )}
            {!isDropdownExpand && (
              <Icon
                data-testid="expand-cdm"
                aria-label="Expand CDM"
                iconName="ChevronDown"
                className={dropdownIconClass}
                onClick={() => onChangeCdmDropdown(cdm)}
              />
            )}
          </CheckTextIconContainer>
          <CdmDivider />
        </SingleCdmContainer>
        {isDropdownExpand && <EditCdmForm cdm={cdm} />}
      </>
    );
  };

  return (
    <>
      {isInitialPageVisible && (
        <InitialCdmStepContainer data-testid="cdm-step-initial">
          <InitialCdmStepTextStyled> Add a new or existing CDM to get started.</InitialCdmStepTextStyled>
          <InitialCdmStepCommandBar items={initialCdmCommandBarItems} ariaLabel="Initial Cdm Command Bar" />
        </InitialCdmStepContainer>
      )}

      {!isInitialPageVisible && (
        <InProcessCdmStepCommandBar
          items={inProcessCdmCommandBarItems}
          ariaLabel="In Process Cdm Command Bar"
          data-testid="cdm-step-in-process"
        />
      )}

      {!isInitialPageVisible && cdmList.length > 0 && (
        <CdmListContainer>
          {cdmList?.map((cdm) => (
            <div key={cdm.id}>{renderCdmDropdown(cdm)}</div>
          ))}
        </CdmListContainer>
      )}

      {isAddExistingCdmFormVisible && <AddExistingCdmForm handleCancelAddExistingCdm={handleCancelAddExistingCdm} />}
    </>
  );
}

const InitialCdmStepContainer = styled.div`
  display: flex;
  padding-top: 128px;
  padding-bottom: 128px;
  flex-direction: column;
  align-items: center;
`;

const InitialCdmStepTextStyled = styled(Text)`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #323130;
`;

const InitialCdmStepCommandBar = styled(CommandBar)`
  display: flex;
  padding-top: 20px;
  margin-left: -32px;
`;

const InProcessCdmStepCommandBar = styled(CommandBar)`
  display: flex;
  margin-left: -32px;
  padding-top: 16px;
  padding-bottom: 30px;
`;

const CdmListContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 30px;
  padding-bottom: 30px;
`;

const SingleCdmContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  align-items: space-between;
`;

const CheckTextIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const CheckTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const SingleCdmTextStyled = styled(Text)`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #323130;
  padding-left: 20px;
`;

const CdmDivider = styled.div`
  border: 1px solid #e5e5e5;
`;
